import React, {useCallback, useEffect, useMemo, useState} from 'react';
import {
    Alert, Box, Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle,
    MenuItem, Stack, Table, TableBody, TableCell, TableHead, TableRow, TextField, Typography,
} from '@mui/material';
import {
    buildColumnKey,
    confirmBankAccountMapping,
    fetchColumnDefs,
    importBankStatement,
    saveGlPreview,
    type GlColumnDef,
    type GlImportResultVO,
    type GlSheetRowVO,
    type UnmatchedBankAccountVO,
} from '@/services/accountService/glInvoiceService';
import {FileUploadDialog} from '@/components/common';

interface BankStatementImportDialogProps {
    open: boolean;
    companyId: number;
    annualId: number;
    onClose: () => void;
    onSaved: (message: string) => void;
}

function fmtAmt(v: number | undefined | null): string {
    if (v == null || Number.isNaN(Number(v))) return '';
    return Number(v).toFixed(2);
}

export default function BankStatementImportDialog(props: BankStatementImportDialogProps) {
    const {open, companyId, annualId, onClose, onSaved} = props;

    const [uploadOpen, setUploadOpen] = useState(false);
    const [saving, setSaving] = useState(false);
    const [confirming, setConfirming] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [result, setResult] = useState<GlImportResultVO | null>(null);
    const [bankCols, setBankCols] = useState<GlColumnDef[]>([]);
    const [mappingDraft, setMappingDraft] = useState<Record<string, string>>({});
    const [bankType, setBankType] = useState<'BOC' | 'HSBC' | 'HANGSENG'>('HSBC');

    useEffect(() => {
        if (!open) return;
        setError(null);
        setResult(null);
        setMappingDraft({});
        setUploadOpen(true);
        fetchColumnDefs()
            .then(defs => setBankCols(defs.filter(d => d.fsCode === 'BS-CA-CASH'
                && d.glCode
                && !d.glCode.toUpperCase().endsWith('-RATE'))))
            .catch(() => setBankCols([]));
    }, [open]);

    const rows: GlSheetRowVO[] = result?.previewData?.list ?? [];
    const unmatched: UnmatchedBankAccountVO[] = result?.unmatchedBankAccounts ?? [];
    const canSave = !!result?.importToken && unmatched.length === 0 && rows.length > 0;
    const bankLabel = result?.bankType || bankType;

    const bankColOptions = useMemo(() => bankCols.map(c => ({
        key: buildColumnKey(c),
        label: `${c.aliasEn || c.nameEn || c.glCode} (${c.glCode})`,
    })), [bankCols]);

    const handleUpload = useCallback(async (files: File[]) => {
        setError(null);
        const data = await importBankStatement(files, companyId, annualId, bankType);
        setResult(data);
        const draft: Record<string, string> = {};
        (data.unmatchedBankAccounts ?? []).forEach(u => {
            draft[u.accountKey] = u.mappedColumnKey || '';
        });
        setMappingDraft(draft);
        return data;
    }, [companyId, annualId, bankType]);

    const handleConfirmMapping = useCallback(async () => {
        if (!result?.importToken) return;
        const mappings = Object.entries(mappingDraft)
            .filter(([, columnKey]) => !!columnKey)
            .map(([accountKey, columnKey]) => ({accountKey, columnKey}));
        if (mappings.length === 0) {
            setError('Please map at least one bank account to a GL column.');
            return;
        }
        setConfirming(true);
        setError(null);
        try {
            const data = await confirmBankAccountMapping({
                importToken: result.importToken,
                companyId,
                annualId,
                mappings,
            });
            setResult(data);
            const draft: Record<string, string> = {};
            (data.unmatchedBankAccounts ?? []).forEach(u => {
                draft[u.accountKey] = u.mappedColumnKey || '';
            });
            setMappingDraft(draft);
        } catch (e: any) {
            setError(e?.message || 'Confirm mapping failed');
        } finally {
            setConfirming(false);
        }
    }, [result, mappingDraft, companyId, annualId]);

    const handleSave = useCallback(async () => {
        if (!result?.previewData || !canSave) return;
        setSaving(true);
        setError(null);
        try {
            const saveResult = await saveGlPreview({
                companyId,
                annualId,
                rows: result.previewData.list ?? [],
                columnMetaMap: result.previewData.columnMetaMap,
                // Match same date+description rows → update/skip; do NOT soft-delete other GL rows
                replaceExisting: true,
                softDeleteUnmatched: false,
            });
            const unchanged = (saveResult.warnings ?? [])
                .find(w => /Unchanged \d+ matched rows/i.test(w));
            onSaved(`Bank statement imported — ${saveResult.insertedCount ?? 0} new, ${saveResult.updatedCount ?? 0} updated`
                + (unchanged ? ` (${unchanged})` : ''));
            onClose();
        } catch (e: any) {
            setError(e?.message || 'Save failed');
        } finally {
            setSaving(false);
        }
    }, [result, canSave, companyId, annualId, onSaved, onClose]);

    return (
        <>
            <Dialog open={open && !uploadOpen} onClose={saving ? undefined : onClose} maxWidth="lg" fullWidth>
                <DialogTitle>
                    Import Bank Statement ({bankLabel})
                    {result?.bankCompanyName ? ` — ${result.bankCompanyName}` : ''}
                </DialogTitle>
                <DialogContent dividers>
                    <Stack spacing={2}>
                        {error && <Alert severity="error">{error}</Alert>}
                        {(result?.warnings?.length ?? 0) > 0 && (
                            <Alert severity="warning">
                                <Box component="ul" sx={{m: 0, pl: 2}}>
                                    {result!.warnings!.map((w, i) => <li key={i}>{w}</li>)}
                                </Box>
                            </Alert>
                        )}

                        {(result?.bankSummaries?.length ?? 0) > 0 && (
                            <Box>
                                <Typography variant="subtitle2" sx={{mb: 1}}>Account balance check</Typography>
                                <Table size="small">
                                    <TableHead>
                                        <TableRow>
                                            <TableCell>Account</TableCell>
                                            <TableCell>B/F Date</TableCell>
                                            <TableCell align="right">Opening</TableCell>
                                            <TableCell align="right">Closing</TableCell>
                                            <TableCell align="right">Var Excel</TableCell>
                                            <TableCell align="right">Var Statement</TableCell>
                                            <TableCell align="right">Diff</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {result!.bankSummaries!.map(s => (
                                            <TableRow key={s.accountKey}>
                                                <TableCell>{s.accountType || s.accountKey}</TableCell>
                                                <TableCell>{s.broughtForwardDate}</TableCell>
                                                <TableCell align="right">{fmtAmt(s.openingBalance)}</TableCell>
                                                <TableCell align="right">{fmtAmt(s.closingBalance)}</TableCell>
                                                <TableCell align="right">{fmtAmt(s.varianceExcel)}</TableCell>
                                                <TableCell align="right">{fmtAmt(s.varianceStatement)}</TableCell>
                                                <TableCell align="right" sx={{
                                                    color: Math.abs(Number(s.varianceDiff ?? 0)) > 0.05 ? 'error.main' : 'inherit',
                                                }}>
                                                    {fmtAmt(s.varianceDiff)}
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </Box>
                        )}

                        {unmatched.length > 0 && (
                            <Box>
                                <Typography variant="subtitle2" sx={{mb: 1}}>
                                    Map PDF accounts to GL bank columns
                                </Typography>
                                <Stack spacing={1}>
                                    {unmatched.map(u => (
                                        <Stack key={u.accountKey} direction="row" spacing={1} alignItems="center">
                                            <Typography variant="body2" sx={{minWidth: 220}}>
                                                {u.accountType} {u.accountNumber || ''}
                                            </Typography>
                                            <TextField
                                                select
                                                size="small"
                                                label="GL column"
                                                value={mappingDraft[u.accountKey] || ''}
                                                onChange={(e) => setMappingDraft(prev => ({
                                                    ...prev,
                                                    [u.accountKey]: e.target.value,
                                                }))}
                                                sx={{minWidth: 280}}
                                            >
                                                <MenuItem value="">— Select —</MenuItem>
                                                {bankColOptions.map(o => (
                                                    <MenuItem key={o.key} value={o.key}>{o.label}</MenuItem>
                                                ))}
                                            </TextField>
                                        </Stack>
                                    ))}
                                </Stack>
                                <Button
                                    sx={{mt: 1, textTransform: 'none'}}
                                    variant="outlined"
                                    disabled={confirming}
                                    onClick={handleConfirmMapping}
                                    startIcon={confirming ? <CircularProgress size={14}/> : undefined}
                                >
                                    Apply mapping
                                </Button>
                            </Box>
                        )}

                        <Box>
                            <Typography variant="subtitle2" sx={{mb: 1}}>
                                Preview rows ({rows.length})
                            </Typography>
                            <Box sx={{maxHeight: 320, overflow: 'auto'}}>
                                <Table size="small" stickyHeader>
                                    <TableHead>
                                        <TableRow>
                                            <TableCell>Date</TableCell>
                                            <TableCell>Description</TableCell>
                                            <TableCell>Columns</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {rows.map((r, idx) => (
                                            <TableRow key={idx}>
                                                <TableCell sx={{whiteSpace: 'nowrap'}}>{r.date}</TableCell>
                                                <TableCell>{r.description}</TableCell>
                                                <TableCell>
                                                    {Object.entries(r.columnValues || {})
                                                        .map(([k, v]) => `${k}=${Number(v).toFixed(2)}`)
                                                        .join('; ')}
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                        {rows.length === 0 && (
                                            <TableRow>
                                                <TableCell colSpan={3}>
                                                    <Typography variant="body2" color="text.secondary">
                                                        No rows yet — map accounts above, then Apply mapping.
                                                    </Typography>
                                                </TableCell>
                                            </TableRow>
                                        )}
                                    </TableBody>
                                </Table>
                            </Box>
                        </Box>
                    </Stack>
                </DialogContent>
                <DialogActions>
                    <Button onClick={onClose} disabled={saving} sx={{textTransform: 'none'}}>Cancel</Button>
                    <Button
                        onClick={() => setUploadOpen(true)}
                        disabled={saving}
                        sx={{textTransform: 'none'}}
                    >
                        Re-upload
                    </Button>
                    <Button
                        variant="contained"
                        disabled={!canSave || saving}
                        onClick={handleSave}
                        startIcon={saving ? <CircularProgress size={14} color="inherit"/> : undefined}
                        sx={{textTransform: 'none'}}
                    >
                        Save to GL
                    </Button>
                </DialogActions>
            </Dialog>

            <Dialog
                open={uploadOpen}
                onClose={() => {
                    if (!result) onClose();
                    else setUploadOpen(false);
                }}
                maxWidth="sm"
                fullWidth
            >
                <DialogTitle>Import Bank Statement PDF</DialogTitle>
                <DialogContent>
                    <Stack spacing={2} sx={{pt: 1}}>
                        <TextField
                            select
                            size="small"
                            label="Bank"
                            value={bankType}
                            onChange={(e) => setBankType(e.target.value as 'BOC' | 'HSBC' | 'HANGSENG')}
                            sx={{maxWidth: 280}}
                        >
                            <MenuItem value="HSBC">HSBC</MenuItem>
                            <MenuItem value="HANGSENG">Hang Seng</MenuItem>
                            <MenuItem value="BOC">BOC (Bank of China)</MenuItem>
                        </TextField>
                        <FileUploadDialog
                            open={uploadOpen}
                            embedded
                            onClose={() => {
                                setUploadOpen(false);
                                if (!result) onClose();
                            }}
                            onSuccess={() => setUploadOpen(false)}
                            onUpload={handleUpload}
                            multiple
                            title={`Upload ${bankType} PDF`}
                            accept=".pdf"
                            acceptLabel="Accepted: .pdf only (text-selectable) · one or more files · Max 20MB each"
                        />
                    </Stack>
                </DialogContent>
            </Dialog>
        </>
    );
}
